import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/types/auth';
import { Feature } from '@/types/auth';

interface AuthState {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    setUser: (user: User | null) => void;
    setLoading: (loading: boolean) => void;
    logout: () => void;
    initialize: () => void;
    hasFeature: (feature: Feature) => boolean;
    getFeatures: () => Feature[];
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            isAuthenticated: false,
            isLoading: true,

            setUser: (user) => {
                // Debug logging
                if (user?.client?.featureAccess) {
                    console.log(
                        'User features loaded:',
                        user.client.featureAccess.map((f) => f.feature),
                    );
                } else {
                    console.log('No features found for user:', user?.email);
                }

                set({
                    user,
                    isAuthenticated: !!user,
                    isLoading: false,
                });
            },

            setLoading: (isLoading) => set({ isLoading }),

            logout: () => {
                // Clear localStorage tokens
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');

                // Reset auth state
                set({
                    user: null,
                    isAuthenticated: false,
                    isLoading: false,
                });
            },

            initialize: () => {
                // Check if tokens exist on app startup
                const accessToken = localStorage.getItem('accessToken');
                const refreshToken = localStorage.getItem('refreshToken');

                if (!accessToken || !refreshToken) {
                    set({
                        user: null,
                        isAuthenticated: false,
                        isLoading: false,
                    });
                } else {
                    // Tokens exist, stop loading but let useCurrentUser fetch user data
                    set({ isLoading: false });
                }

                // If tokens exist, the API interceptor will handle validation
                // and we'll get user data from React Query
            },

            hasFeature: (feature: Feature) => {
                const { user } = get();

                // Admins and super admins have access to everything
                if (user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') {
                    return true;
                }

                // No user or no client profile
                if (!user?.client?.featureAccess) {
                    return false;
                }

                // Extract feature list
                const features = user.client.featureAccess.map((fa) => fa.feature);

                // Check if user has Feature.ALL (grants all access)
                if (features.includes(Feature.ALL)) {
                    return true;
                }

                // Check if user has the specific feature
                return features.includes(feature);
            },

            getFeatures: () => {
                const { user } = get();

                // Admins have all features
                if (user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') {
                    return Object.values(Feature);
                }

                // No user or no client profile
                if (!user?.client?.featureAccess) {
                    return [];
                }

                // Extract feature list
                const features = user.client.featureAccess.map((fa) => fa.feature);

                // If user has Feature.ALL, return all features
                if (features.includes(Feature.ALL)) {
                    return Object.values(Feature);
                }

                return features;
            },
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({
                user: state.user,
                isAuthenticated: state.isAuthenticated,
            }),
        },
    ),
);
